<main class="page">
  <div class="glow" aria-hidden="true"></div>
  <div class="grain" aria-hidden="true"></div>
  <app-brand class="backdrop" [draw]="true" aria-hidden="true" />

  <section class="card">
    <div class="brand">
      <app-brand class="logo" />
      <span class="product">SSO</span>
    </div>

    @if (clientBadge(); as detail) {
      <div class="client">
        <img
          class="client-logo"
          [src]="detail.clientLogoUrl"
          [alt]="'Logo di ' + detail.clientDisplayName"
          referrerpolicy="no-referrer"
          (error)="logoBroken.set(true)"
        />
        <span class="client-name">{{ detail.clientDisplayName }}</span>
      </div>
    }

    <h1 class="title">
      @if (accessRequested()) {
        Richiesta inoltrata
      } @else if (totp()) {
        Verifica in due passaggi
      } @else {
        Accedi
      }
    </h1>
    <p class="lead">
      @if (accessRequested()) {
        @if (client(); as detail) {
          Il tuo account non è ancora abilitato a <b>{{ detail.clientDisplayName }}</b
          >.
        } @else {
          Il tuo account non è ancora abilitato a questa applicazione.
        }
      } @else if (clientBadge()) {
        Autenticati per continuare.
      } @else if (client(); as detail) {
        Autenticati per continuare verso <b>{{ detail.clientDisplayName }}</b
        >.
      } @else if (hasAuthorizationRequest) {
        Autenticati per continuare verso l'applicazione.
      } @else {
        Autenticati per aprire una sessione.
      }
    </p>

    @if (errorMessage(); as message) {
      <p class="banner banner-error" role="alert">
        <svg class="banner-icon" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="10" r="7.6" fill="none" stroke="currentColor" stroke-width="1.5" />
          <path d="M10 6.4v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          <circle cx="10" cy="13.9" r="0.95" fill="currentColor" />
        </svg>
        <span>{{ message }}</span>
      </p>
    }

    @if (sessionCreated()) {
      <p class="banner banner-success" role="status">
        <svg class="banner-icon" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="10" r="7.6" fill="none" stroke="currentColor" stroke-width="1.5" />
          <path
            d="m6.6 10.3 2.3 2.3 4.5-4.9"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>
          Accesso effettuato. Torna all'applicazione da cui sei partito: verrai riconosciuto
          automaticamente.
        </span>
      </p>
    }

    @if (accessRequested()) {
      <p class="banner banner-success" role="status">
        <svg class="banner-icon" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="10" r="7.6" fill="none" stroke="currentColor" stroke-width="1.5" />
          <path
            d="m6.6 10.3 2.3 2.3 4.5-4.9"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>
          La tua richiesta di accesso è stata inoltrata all'amministratore. Riceverai l'accesso non
          appena verrà approvata.
        </span>
      </p>
    }

    @if (totp(); as step) {
      <div class="totp">
        @if (step.step === 'ENROLL') {
          <p class="totp-lead">
            Per proteggere il tuo account serve una verifica in più. Apri un'app di autenticazione
            (ad esempio Google o Microsoft Authenticator) e inquadra questo codice.
          </p>
          @if (step.uri) {
            <app-qr-code
              class="totp-qr"
              [value]="step.uri"
              label="Codice QR da inquadrare con l'app di autenticazione"
            />
          }
          @if (step.secret) {
            <details class="totp-manual">
              <summary>Non riesci a inquadrarlo?</summary>
              <p>Nell'app scegli l'inserimento manuale e copia questa chiave:</p>
              <div class="totp-secret">
                <code>{{ groupedSecret() }}</code>
                <button type="button" class="link" (click)="copySecret()">
                  {{ secretCopied() ? 'Copiata' : 'Copia' }}
                </button>
              </div>
            </details>
          }
        } @else {
          <p class="totp-lead">
            Inserisci il codice di 6 cifre che vedi nell'app di autenticazione.
          </p>
        }

        <form class="form" (ngSubmit)="submitTotp()" novalidate>
          <div class="field">
            <label for="totp-code">
              {{ step.step === 'ENROLL' ? 'Codice mostrato dall’app' : 'Codice' }}
            </label>
            <input
              type="text"
              id="totp-code"
              class="totp-input"
              [formControl]="totpCode"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              placeholder="000000"
              #totpInput
              (input)="onTotpInput()"
            />
          </div>

          <button type="submit" class="submit" [disabled]="locked()">
            @if (submitting() || redirecting()) {
              <span class="spinner" aria-hidden="true"></span>
            }
            <span>
              @if (redirecting()) {
                Reindirizzamento…
              } @else if (submitting()) {
                Verifica in corso…
              } @else {
                Verifica
              }
            </span>
          </button>
        </form>

        <button type="button" class="link back" [disabled]="locked()" (click)="backToPassword()">
          Torna indietro
        </button>
      </div>
    }

    @if (
      !totp() &&
      !requestExpired() &&
      !sessionCreated() &&
      !accessRequested() &&
      providers().length > 0
    ) {
      <div class="providers">
        @for (provider of providers(); track provider.type) {
          <button
            type="button"
            class="provider"
            [disabled]="locked()"
            (click)="startFederated(provider.type)"
          >
            @if (federating() === provider.type) {
              <span class="spinner" aria-hidden="true"></span>
            } @else if (provider.type === 'ENTRA') {
              <svg class="provider-icon" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            }
            <span>{{ provider.label }}</span>
          </button>
        }
      </div>
      <div class="divider" role="separator"><span>oppure</span></div>
    }

    @if (!totp() && !requestExpired() && !sessionCreated() && !accessRequested()) {
      <form class="form" (ngSubmit)="submit()" [formGroup]="form" novalidate>
        <div class="field">
          <label for="username">Username</label>
          <div class="input-wrap">
            <svg class="lead-icon" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="10" cy="7" r="3.1" fill="none" stroke="currentColor" stroke-width="1.4" />
              <path
                d="M4 16.4c0-2.7 2.7-4.5 6-4.5s6 1.8 6 4.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
            <input
              type="text"
              id="username"
              formControlName="username"
              autocomplete="username"
              autocapitalize="none"
              spellcheck="false"
              [attr.aria-invalid]="isInvalid('username')"
            />
          </div>
          @if (isInvalid('username')) {
            <span class="field-error">Inserisci il tuo username</span>
          }
        </div>

        <div class="field">
          <label for="password">Password</label>
          <div class="input-wrap">
            <svg class="lead-icon" viewBox="0 0 20 20" aria-hidden="true">
              <rect
                x="4.4"
                y="8.8"
                width="11.2"
                height="7"
                rx="1.8"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
              />
              <path
                d="M7.2 8.8V6.9a2.8 2.8 0 0 1 5.6 0v1.9"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
            <input
              [type]="passwordVisible() ? 'text' : 'password'"
              id="password"
              formControlName="password"
              autocomplete="current-password"
              (keyup)="checkCapsLock($event)"
              (keydown)="checkCapsLock($event)"
              [attr.aria-invalid]="isInvalid('password')"
            />
            <button
              type="button"
              class="reveal"
              (click)="togglePasswordVisibility()"
              [attr.aria-label]="passwordVisible() ? 'Nascondi password' : 'Mostra password'"
              [attr.aria-pressed]="passwordVisible()"
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M2.6 10S5.6 4.9 10 4.9 17.4 10 17.4 10 14.4 15.1 10 15.1 2.6 10 2.6 10Z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linejoin="round"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="2.4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                />
                @if (passwordVisible()) {
                  <path
                    d="M3.6 16.4 16.4 3.6"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                }
              </svg>
            </button>
          </div>
          @if (isInvalid('password')) {
            <span class="field-error">Inserisci la tua password</span>
          } @else if (capsLock()) {
            <span class="field-hint">Il BLOC MAIUSC è attivo.</span>
          }
        </div>

        <button type="submit" class="submit" [disabled]="locked()">
          @if (submitting() || redirecting()) {
            <span class="spinner" aria-hidden="true"></span>
          }
          <span>
            @if (redirecting()) {
              Reindirizzamento…
            } @else if (submitting()) {
              Accesso in corso…
            } @else {
              Accedi
            }
          </span>
        </button>
      </form>
    }

    <p class="note">
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <rect
          x="4.4"
          y="8.8"
          width="11.2"
          height="7"
          rx="1.8"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
        />
        <path
          d="M7.2 8.8V6.9a2.8 2.8 0 0 1 5.6 0v1.9"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
      <span>Accesso unico: vale per tutte le applicazioni collegate.</span>
    </p>
  </section>
</main>
